/**
 * services/channel-colors.js — per-channel color + label, read-only client.
 *
 * v2 (2026-04-14, task #98) — account-shared, server-backed, READ-ONLY in
 * the PWA. The end-user dashboard owns writes (account owner only).
 *
 *   - Backed by Supabase inbox_channel_meta (color_hex + label per channel)
 *   - localStorage is an instant-render cache; server is source of truth
 *   - Collisions allowed — channels can share a color to create groupings
 *   - 12 muted colors curated by hue family, each with a Pantone-ish name
 *   - Channel label is a free-form 30-char subtext set by the account owner
 *     (e.g. "Sales", "Support", "ODIV Primary Channel")
 *   - Deterministic hash default (hash(phone_number_id) % 12) still applies
 *     to channels with no color override so colors are stable from day one
 *
 * Consumers (PWA, all read-only):
 *   - ChatRow: 3px left-edge accent bar in channel color (no text label)
 *   - Thread: thin colored line under header
 *   - ChatList filter pills: prepend label if set
 *   - ContactPanel: shows label as channel subtitle
 */

import { atom } from 'nanostores';
import { getAccountId, $session } from '../stores/session.js';
import * as api from './api.js';
import { isPermissionDeniedError, markDegraded, markHealthy } from './permission-degraded.js';

// ── Palette ──────────────────────────────────────────────────────────────────
// 20 muted colors. Loosely grouped by hue family but no enforced uniqueness —
// owners can use the same color across multiple channels for grouping
// (sales = blue, support = green, etc). Every swatch reads as a HINT,
// not a brand statement. Kept in sync with js/dashboard.js CHANNEL_PALETTE
// and the dashboard-account.js validation set.
export const CHANNEL_COLOR_PALETTE = Object.freeze([
  // Warm reds & oranges
  '#c76666', // Rose Dust
  '#b07060', // Clay Brown
  '#d4936e', // Apricot Wash
  '#d08a5c', // Burnt Sienna
  '#a8856e', // Caramel Beige
  // Yellows & earthy
  '#c9a455', // Antique Gold
  '#b0a355', // Mustard Field
  '#8a9654', // Olive Branch
  '#78a868', // Moss Garden
  '#6e8a7e', // Sage Hollow
  // Greens & teals
  '#5b8970', // Forest Mist
  '#5e9b8a', // Pacific Teal
  '#5284a8', // Slate Blue
  '#4f7088', // Stormy Sky
  '#7a92a8', // Misty Harbor
  // Blues, purples & neutrals
  '#6f7fb0', // Dusty Cornflower
  '#8a74a6', // French Lavender
  '#957a9c', // Heather
  '#a87898', // Mauve Mist
  '#7a7f8a', // Steel Mist
]);

// ── Reactive store ───────────────────────────────────────────────────────────
// Map of phone_number_id → { color, label } meta. Both fields are nullable:
//   color: hex string OR null/missing (use deterministic hash default)
//   label: 30-char string OR null/missing (no label displayed)
//
// Backwards-compat alias `$channelColorOverrides` is kept as a SECOND atom
// derived from $channelMeta so older imports don't break during the swap.
export const $channelMeta = atom({}); // { [phoneId]: { color, label } }
export const $channelColorOverrides = atom({}); // legacy alias, color-only view

// ── localStorage cache ────────────────────────────────────────────────────
function _storageKey(accountId) {
  return `inbox_v2_channel_meta_${accountId || 'anon'}`;
}

function _load(accountId) {
  if (!accountId) return {};
  try {
    const raw = localStorage.getItem(_storageKey(accountId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function _save(accountId, map) {
  if (!accountId) return;
  try {
    localStorage.setItem(_storageKey(accountId), JSON.stringify(map || {}));
  } catch { /* quota */ }
}

/** Build the legacy color-only view from the full meta map. */
function _toColorMap(meta) {
  const out = {};
  for (const [k, v] of Object.entries(meta || {})) {
    if (v?.color) out[k] = v.color;
  }
  return out;
}

/** Normalize the server's array → { [phoneId]: {color, label} } map. */
function _serverRowsToMap(rows) {
  const out = {};
  if (!Array.isArray(rows)) return out;
  for (const r of rows) {
    if (!r?.phone_number_id) continue;
    out[r.phone_number_id] = {
      color: r.color_hex || null,
      label: r.label || null,
    };
  }
  return out;
}

function _commitMap(accountId, map) {
  $channelMeta.set(map);
  $channelColorOverrides.set(_toColorMap(map));
  _save(accountId, map);
}

/**
 * Two-phase hydrate:
 *   1. Instant paint from localStorage cache
 *   2. Async fetch from /channel-meta, replace store + cache with server state
 *
 * Errors on the server fetch are swallowed — cache stays in place so the
 * UI still renders the last-known meta offline.
 */
export function hydrateChannelMeta() {
  const accountId = getAccountId();
  // Phase 1: cache paint (instant)
  const cached = _load(accountId);
  $channelMeta.set(cached);
  $channelColorOverrides.set(_toColorMap(cached));
  if (!accountId) return Promise.resolve();

  // Phase 2: server refresh
  return (async () => {
    try {
      const raw = await api.fetchChannelMeta();
      const rows = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : []);
      _commitMap(accountId, _serverRowsToMap(rows));
      markHealthy('channel-meta');
    } catch (e) {
      console.warn('[v2 channel-meta] server hydrate failed:', e.message);
      // #168 — surface 403 as degraded-permissions hint. Without channel
      // metadata the chat list loses its color stripes + channel chips.
      if (isPermissionDeniedError(e)) {
        markDegraded('channel-meta', e.message);
      }
    }
  })();
}

// Legacy alias for older callers
export const hydrateChannelColors = hydrateChannelMeta;

// Re-hydrate whenever the session changes (login, logout, account switch).
$session.listen((session) => {
  const accountId = session?.accountId || null;
  const cached = _load(accountId);
  $channelMeta.set(cached);
  $channelColorOverrides.set(_toColorMap(cached));
  if (accountId) hydrateChannelMeta();
});

// Hydrate at import time so the first render has current data.
hydrateChannelMeta();

// ── Deterministic default ────────────────────────────────────────────────────
/**
 * Simple 32-bit FNV-1a hash of a string. Good enough for assigning a stable
 * palette slot to each phone_number_id — we don't need cryptographic quality.
 */
function _hash(s) {
  if (!s) return 0;
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function _defaultColorFor(phoneNumberId) {
  const h = _hash(String(phoneNumberId || ''));
  return CHANNEL_COLOR_PALETTE[h % CHANNEL_COLOR_PALETTE.length];
}

// ── Public getters ───────────────────────────────────────────────────────────

/**
 * Normalize any channel identifier to the Meta `phone_number_id` that
 * `inbox_channel_meta` and the deterministic palette hash are keyed by.
 *
 * #154 fix — chat rows carry `inbox_messages.channel_id` which after #134
 * holds the Partner Panel channel_id UUID (24-char hex like
 * "69485a97a62ef012efdf2ac8"), NOT the Meta phone_number_id. The dashboard
 * writes `inbox_channel_meta` keyed by phone_number_id, so a direct lookup
 * with the UUID misses and falls through to the hash default — meaning the
 * chat row's left-edge stripe color and in-row label were NEVER actually
 * reflecting what the primary user set in the dashboard.
 *
 * This helper routes any id (UUID or phone_number_id) through the session's
 * channel list, which tolerates both shapes (see getChannelInfo), and
 * returns the canonical phone_number_id. Callers that pass an already-
 * canonical id pay zero cost (the lookup is an in-memory array scan over
 * ~a handful of channels per account).
 *
 * Fallback: if the id isn't in the session (e.g. stale cache, a channel
 * that was just added in the dashboard), we return the input as-is so the
 * hash default at least produces a stable (if wrong-palette) color instead
 * of blowing up.
 */
function _resolvePhoneId(id) {
  if (!id) return null;
  // If the id is already a direct key in $channelMeta, it's a phone_number_id.
  const meta = $channelMeta.get();
  if (meta[id] !== undefined) return id;
  // Otherwise try to resolve via the session channel list.
  const info = getChannelInfo(id);
  return info?.phone_number_id || id;
}

/**
 * Resolve the color for a channel. Account override wins; otherwise the
 * deterministic hash default. Always returns a hex string so callers never
 * have to null-check. Accepts either a phone_number_id or a Partner Panel
 * channel_id UUID — both are normalized to phone_number_id internally.
 */
export function getChannelColor(id) {
  if (!id) return CHANNEL_COLOR_PALETTE[0];
  const phoneId = _resolvePhoneId(id);
  const meta = $channelMeta.get()[phoneId];
  return meta?.color || _defaultColorFor(phoneId);
}

/**
 * Resolve the editable label for a channel. Returns null if no label has
 * been set by the account owner — callers should hide the label UI in
 * that case (no fallback string). Accepts either a phone_number_id or a
 * Partner Panel channel_id UUID.
 */
export function getChannelLabel(id) {
  if (!id) return null;
  const phoneId = _resolvePhoneId(id);
  return $channelMeta.get()[phoneId]?.label || null;
}

// PWA is read-only — these throw if called from agent code so we catch
// accidental writes during the refactor. Real edits go through the
// end-user dashboard via dashboard-account.js (#98 v2).
export async function setChannelColor() {
  throw new Error('setChannelColor: writes happen in the end-user dashboard, not the PWA');
}
export async function clearAllChannelColors() {
  throw new Error('clearAllChannelColors: writes happen in the end-user dashboard, not the PWA');
}

// ── Display helpers ──────────────────────────────────────────────────────────

/**
 * Build a 2-letter prefix badge from a channel display name:
 *   "Odiv AI Automations"  → "OA"
 *   "Prinstaindia"         → "PR"
 *   "15517868406"          → "15"
 *   ""                     → "??"
 */
export function channelPrefix(displayName) {
  const name = String(displayName || '').trim();
  if (!name) return '??';
  const words = name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * Look up a channel by identifier from the session's channel list.
 *
 * Historical naming quirk: the field `chat.channelPhoneId` in the v2 store
 * (and `channel_phone_id` in the proxy's messages response) actually holds
 * the Partner Panel channel_id UUID, NOT the Meta phone_number_id. We match
 * on both fields so whichever the caller passes resolves correctly. Returns
 * null if the session isn't loaded yet or the id isn't known.
 */
export function getChannelInfo(id) {
  if (!id) return null;
  const channels = $session.get()?.channels || [];
  return channels.find(c => c.channel_id === id || c.phone_number_id === id) || null;
}

/**
 * One-stop-shop for chat row + filter pill rendering. Returns:
 *   - color:       hex (override or hash default)
 *   - prefix:      2-letter prefix from display_name (still useful for
 *                  filter pills where text labels make sense)
 *   - displayName: channel's display name (e.g. "Odiv AI Automations")
 *   - label:       account-owner-set channel label (e.g. "Sales") or null
 *
 * Falls back gracefully when the channel isn't in the session (e.g. stale
 * cache after a channel was added in the dashboard).
 */
export function getChannelBadge(phoneNumberId) {
  const info = getChannelInfo(phoneNumberId);
  const displayName = info?.display_name || phoneNumberId || '';
  return {
    color:       getChannelColor(phoneNumberId),
    prefix:      channelPrefix(displayName),
    displayName,
    label:       getChannelLabel(phoneNumberId),
  };
}

/**
 * Return the full list of channels from the session, with color + prefix
 * pre-computed. Used by the Settings > Channel colors assignment screen
 * and by the chat-list channel filter pills.
 */
export function listChannelsWithBadges() {
  const channels = $session.get()?.channels || [];
  return channels.map(c => ({
    phoneNumberId: c.phone_number_id,
    displayName:   c.display_name || c.phone_number_id,
    wabaId:        c.waba_id || null,
    channelId:     c.channel_id || null,
    color:         getChannelColor(c.phone_number_id),
    prefix:        channelPrefix(c.display_name || c.phone_number_id),
    label:         getChannelLabel(c.phone_number_id),
  }));
}
